import { useCallback, useContext, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/react-hooks';

import type {
	TaskAction,
	SetBatchedTaskStatusVariables,
	SetBatchedTaskStatus,
	InlineTask,
} from '@confluence/fabric-media-support';
import {
	TaskBatcher,
	SetBatchedTaskStatusMutation,
	PageUpdateTrigger,
	TaskStatus,
} from '@confluence/fabric-media-support';
import {
	ExperienceTrackerContext,
	SET_TASK_COMPLETE_EXPERIENCE,
} from '@confluence/experience-tracker';

const DEFAULT_TASK_UPDATE_INTERVAL_MS = 3000;

export const getBatchesByContentIds = (taskActions: TaskAction[]) =>
	taskActions.reduce(
		(batches, taskAction) => {
			const { objectKey, state, contentId } = taskAction;

			if (!contentId) {
				return batches;
			}

			// Format the task from the batch
			const task: InlineTask = {
				taskId: objectKey.localId,
				status: state === 'DONE' ? TaskStatus.CHECKED : TaskStatus.UNCHECKED,
			};

			// Save batch to record for later processing.
			return {
				...batches,
				[contentId]: {
					tasks: [...(batches?.[contentId]?.tasks ?? []), task],
					trigger: PageUpdateTrigger.VIEW_PAGE,
					contentId,
				},
			};
		},
		{} as Record<string, SetBatchedTaskStatusVariables>,
	);

export const useTaskBatcher = () => {
	const experienceTracker = useContext(ExperienceTrackerContext);

	const [mutateFunction, { data, loading, error }] = useMutation<
		SetBatchedTaskStatus,
		SetBatchedTaskStatusVariables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		SetBatchedTaskStatusMutation,
	);

	type MutateResponse = ReturnType<typeof mutateFunction>;

	const taskBatcherRef = useRef(new TaskBatcher(DEFAULT_TASK_UPDATE_INTERVAL_MS));
	// Handlers for batched task update. Batches with contentId.
	const handleBatchTaskUpdate = useCallback(
		async (taskActions: TaskAction[]) => {
			experienceTracker.start({ name: SET_TASK_COMPLETE_EXPERIENCE });

			const updateBatches = getBatchesByContentIds(taskActions);

			// Run batches by contentId due to graph limitation.
			const queries: MutateResponse[] = Object.values(updateBatches).map(async (batch) => {
				const result = await mutateFunction({
					variables: batch,
				});

				return result;
			});

			const responses = await Promise.allSettled(queries);

			const errors = responses.reduce((flattenedErrors, response) => {
				if (response.status === 'rejected') {
					return [...flattenedErrors, new Error(response.reason)];
				}

				if (response?.value?.errors) {
					return [...flattenedErrors, ...response.value.errors];
				}

				return flattenedErrors;
			}, [] as Error[]);

			if (errors.length === 0) {
				experienceTracker.succeed({
					name: SET_TASK_COMPLETE_EXPERIENCE,
					attributes: {
						batches: JSON.stringify(updateBatches),
					},
				});
			} else {
				experienceTracker.fail({
					name: SET_TASK_COMPLETE_EXPERIENCE,
					error: new Error(`Could not complete task complete mutation: ${JSON.stringify(errors)}`),
				});
			}
		},
		[mutateFunction, experienceTracker],
	);

	useEffect(() => {
		taskBatcherRef.current.onFlush(handleBatchTaskUpdate);
	}, [taskBatcherRef, handleBatchTaskUpdate]);

	return {
		add: taskBatcherRef.current.add,
		data,
		loading,
		error,
	};
};
